import React, {useEffect, useState} from "react";
import validator from 'validator';
import {selectUserAccount} from "../../../stores/account/account.selectors";
import {useDispatch, useSelector} from "react-redux";
import {updateAccountAsync, updateAddressAsync} from "../../../stores/account/account.thunks";
import _ from 'lodash';
import {FormattedMessage} from "react-intl";
import {resetUserAccountProfileState} from "../../../stores/account/account.slice";
import {ModalType} from "../../../components/AppModal/ModalTypes";
import useAppModalService from "../../../hooks/useAppModalService";
import UserAccountUpdatedPopUp from "../../PopUps/UserAccountUpdateSuccess/UserAccountUpdatedPopUp";

const useMyProfileVM = () => {
    const dispatch = useDispatch();
    const userAccount = useSelector(selectUserAccount);
    const [userData, setUserData] = useState(userAccount);
    const {openModal} = useAppModalService();

    const handleUserDataChange = (e) => {
        const {name, value} = e.target;

        setUserData({
            ...userData,
            [name]: value,
        });
    }
    const handleAddressChange = (e) => {
        const {name, value} = e.target;
        setUserData({
            ...userData,
            address: {
                ...userData.address,
                [name]: value,
            }
        });
    }

    const setCountry = (country) => {
        setUserData({
            ...userData,
            address: {
                ...userData.address,
                country: country,
            }
        });
    }

    const setAddressFields = (fields) => {
        setUserData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                ...fields,
            }
        }));
    }

    // My Account Information  ------------------------------------------------

    const [infoErrors, setInfoErrors] = useState({
        pseudonym: false,
        firstName: false,
        lastName: false,
        email: false,
    });


    useEffect(() => {
        setUserData(userAccount);
    }, [userAccount]);

    useEffect(() => {
    }, [userData]);


    const validateInfoForm = () => {
        let formErrors = {
            pseudonym: !userData.pseudonym,
            firstName: !userData.firstName,
            lastName: !userData.lastName,
            email: !validator.isEmail(userData.email),
            phone: !_.isEmpty(userData.address.phone1) ? !validator.isMobilePhone(userData.address.phone1) : false,
        };
        setInfoErrors(formErrors);
        return !Object.values(formErrors).includes(true);
    }
    const handleInfoSubmit = async (e) => {
        e.preventDefault();
        if (validateInfoForm()) {
            await dispatch(updateAccountAsync(userData));
        } else {
            console.log('Form is invalid, show errors');
        }
    }

    // End My Account Information  --------------------------------------------

    // Reset Password   ------------------------------------------------

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const getFieldLabel = (field) => ({
        currentPassword: 'Current Password',
        newPassword: 'Password',
        confirmPassword: 'Confirm Password',
    }[field]);
    const handlePasswordChange = (field, value) => {
        switch (field) {
            case 'currentPassword':
                setCurrentPassword(value);
                setUserData((prev) => ({ ...prev, password: value }));
                break;
            case 'newPassword':
                setNewPassword(value);
                setUserData((prev) => ({ ...prev, newPassword: value }));
                break;
            case 'confirmPassword':
                setConfirmPassword(value);
                setUserData((prev) => ({ ...prev, confirmNewPassword: value }));
                break;
        }

        setPasswordErrors((prev) => ({
            ...prev,
            [field]: getPasswordError(field, value),
        }));
    };
    const getPasswordError = (field, value) => {
        if (validator.isEmpty(value))
            return `${getFieldLabel(field)} Required`;
        if (field === 'newPassword' && !validator.isLength(value, { min: 6 }))
            return 'Password must be at least 6 characters';
        if (field === 'confirmPassword' && value !== newPassword)
            return 'Password does not match';
        return '';
    };
    const validatePasswordChange = () => {
        const currentPasswordError = getPasswordError('currentPassword', currentPassword);
        const newPasswordError = getPasswordError('newPassword', newPassword);
        const confirmPasswordError = getPasswordError('confirmPassword', confirmPassword);

        setPasswordErrors({
            currentPassword: currentPasswordError,
            newPassword: newPasswordError,
            confirmPassword: confirmPasswordError,
        });

        return !(currentPasswordError || newPasswordError || confirmPasswordError);
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!passwordErrors.newPassword && !passwordErrors.confirmPassword) {
            await dispatch(updateAccountAsync(userData));
            }
    }

    // End Reset Password   --------------------------------------------

    // My Address   ------------------------------------------------

    const [addressErrors, setAddressErrors] = useState({});

    const validateAddressForm = () => {
        const newErrors = {};

        if (!userData?.address.address1) {
            newErrors.address = 'Street Address Required';
        }
        if (!userData?.address.city) {
            newErrors.city = 'City Required';
        }
        if (!userData?.address.state || !userData?.address.stateCode) {
            newErrors.state = 'State/Province Required';
        }
        if (!userData?.address.country || !userData?.address.countryCode) {
            newErrors.country = 'Country Required';
        }
        if (!userData?.address.zipCode ||
            (!validator.isPostalCode(userData?.address.zipCode, 'CA') &&
                !validator.isPostalCode(userData?.address.zipCode, 'US'))) {
            newErrors.zipCode = 'ZIP/Postal Code Required';
        }
        if (!userData?.address.phone1 || !validator.isMobilePhone(userData?.address.phone1)) {
            newErrors.phone = 'Phone Required';
        }

        setAddressErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleAddressSubmit = async (e) => {
        e.preventDefault();
        if (validateAddressForm()) {
            await dispatch(updateAddressAsync(userData.address));
        }
    }

    // End My Address   --------------------------------------------

    const showPasswordChangedMessage = () => {
        openModal('PasswordChanged', ModalType.INFO, {
            title: <FormattedMessage id={`popUp.PasswordChanged.title`}/>,
            subtitle: <FormattedMessage id={`popUp.PasswordChanged.subTitle`}/>,
            modalComponent: UserAccountUpdatedPopUp,
            onClose: () => {
                dispatch(resetUserAccountProfileState());
            },
            onOk: () => {
                dispatch(resetUserAccountProfileState());
            }
        });
    }

    const handleSaveChanges = async (e) => {
        const haveAddressDetails = !_.isEmpty(userData?.address?.address1) ||
                                   !_.isEmpty(userData?.address?.city) ||
                                   !_.isEmpty(userData?.address?.state) ||
                                   !_.isEmpty(userData?.address?.country) ||
                                   !_.isEmpty(userData?.address?.zipCode);
        const isValidAddress = haveAddressDetails ? validateAddressForm() : true;
        const isChangingPassword = !_.isEmpty(newPassword) || !_.isEmpty(confirmPassword);
        const isPasswordValid = isChangingPassword ? validatePasswordChange() : true;

        if(validateInfoForm() && isValidAddress && isPasswordValid) {
            await dispatch(updateAccountAsync(userData));
            if (haveAddressDetails)
                await dispatch(updateAddressAsync(userData.address));
            if(isChangingPassword)
                showPasswordChangedMessage();
        }

    }

    return {
        infoErrors,
        handleUserDataChange,
        handleInfoSubmit,

        currentPassword,
        newPassword,
        confirmPassword,
        passwordErrors,
        handlePasswordChange,
        handlePasswordSubmit,
        setCountry,

        addressErrors,
        handleAddressChange,
        handleAddressSubmit,
        setAddressFields,

        userData,
        handleSaveChanges,
    }
}

export default useMyProfileVM;

